import {
  DEMO_BRAND,
  DEMO_CUSTOMER_SEGMENTS,
  DEMO_TRACKED_PROPERTIES,
  formatCompactNumber,
} from '@/lib/fixtures/demo-brand'

export interface PropertyAudience {
  key: string
  name: string
  meta: string
  count: string
  /** 135deg two-stop gradient for the icon tile. */
  gradient: string
  iconKey: 'users' | 'cart-off' | 'check' | 'topology'
}

export interface PropertyConversion {
  key: string
  name: string
  meta: string
  count: string
  gradient: string
  iconKey: 'form' | 'phone' | 'invoice' | 'mail'
}

export interface PropertyContainerTag {
  name: string
  badge: string
  /** "Pixel" | "Event" | etc. — drives the icon. */
  kind: 'pixel' | 'event'
  /** Whether the tag is firing. */
  live: boolean
}

export interface PropertyBrandKit {
  palette: string[]  // hex strings
  typographyHeadings: string
  typographyBody: string
  voice: string
}

export interface Property {
  id: string
  name: string
  domain: string
  initials: string
  /** Card-band gradient. */
  gradient: string
  /** Solid hex used for initials text + brand strip. */
  hex: string
  // Overview
  spend: string
  conversions: string
  topCampaignName: string
  topCampaignSub: string
  // Container
  tags: PropertyContainerTag[]
  triggers: number
  variables: number
  versions: number
  // Brand
  brand: PropertyBrandKit
  // Audiences
  audiences: PropertyAudience[]
  // Conversions
  conversionEvents: PropertyConversion[]
  conversionWindowLabel: string  // "Last 30 days · 610 conversions"
  audienceMetaLabel: string      // "4 audiences · synced from the Atrium pixel"
  // Settings
  status: 'Active' | 'Paused' | 'Archived'
  brandKitLabel: string
}

function audienceIconKey(iconKey: string): PropertyAudience['iconKey'] {
  if (iconKey === 'cart-off' || iconKey === 'check' || iconKey === 'topology') return iconKey
  return 'users'
}

function conversionIconKey(iconKey: string): PropertyConversion['iconKey'] {
  if (iconKey === 'check') return 'invoice'
  if (iconKey === 'cart') return 'invoice'
  if (iconKey === 'play') return 'mail'
  return 'form'
}

export const FALLBACK_AUDIENCES: PropertyAudience[] = DEMO_CUSTOMER_SEGMENTS.slice(0, 4).map((segment) => ({
  key: segment.key,
  name: segment.name,
  meta: segment.meta,
  count: formatCompactNumber(segment.count),
  gradient: segment.gradient,
  iconKey: audienceIconKey(segment.iconKey),
}))

export const FALLBACK_EVENTS: PropertyConversion[] = DEMO_TRACKED_PROPERTIES.map((property) => ({
  key: property.key,
  name: property.name,
  meta: `${property.path} · ${property.primary ? 'primary' : 'secondary'}`,
  count: formatCompactNumber(property.events),
  gradient: property.gradient,
  iconKey: conversionIconKey(property.iconKey),
}))

const SHARED_TAGS: PropertyContainerTag[] = [
  { name: 'Atrium Base Pixel', badge: 'Pixel · all pages', kind: 'pixel', live: true },
  { name: `Conversion — ${DEMO_TRACKED_PROPERTIES[0]?.name ?? 'Primary'}`, badge: `Event · ${DEMO_TRACKED_PROPERTIES[0]?.path ?? '/checkout/success'}`, kind: 'event', live: true },
]

const totalEvents = DEMO_TRACKED_PROPERTIES.reduce((sum, property) => sum + property.events, 0)

export const FALLBACK_PROPERTIES: Property[] = [
  {
    id: 'acme-storefront',
    name: DEMO_BRAND.shortName,
    domain: DEMO_BRAND.domain,
    initials: 'AC',
    gradient: 'linear-gradient(135deg, #0A8A6E, #2BC39A)',
    hex: DEMO_BRAND.primaryColor,
    spend: '$178,000',
    conversions: formatCompactNumber(DEMO_TRACKED_PROPERTIES[0]?.events ?? 0),
    topCampaignName: 'Spring Launch — Roku CTV',
    topCampaignSub: '3.0× ROAS · last 30 days',
    tags: SHARED_TAGS,
    triggers: DEMO_TRACKED_PROPERTIES.length,
    variables: 6,
    versions: 4,
    brand: {
      palette: [DEMO_BRAND.primaryColor, DEMO_BRAND.accentColor, '#2D6CDF', '#7A5BD6', '#1A1A1A'],
      typographyHeadings: 'Inter',
      typographyBody: 'Inter',
      voice: 'Clear, confident, conversion-focused commerce messaging for multi-channel shoppers.',
    },
    audiences: FALLBACK_AUDIENCES,
    conversionEvents: FALLBACK_EVENTS,
    conversionWindowLabel: `Last 30 days · ${formatCompactNumber(totalEvents)} events`,
    audienceMetaLabel: `${FALLBACK_AUDIENCES.length} audiences · synced from the Atrium pixel`,
    status: 'Active',
    brandKitLabel: `${DEMO_BRAND.shortName} — auto-generated`,
  },
]

export function findProperty(id: string): Property | null {
  return FALLBACK_PROPERTIES.find((p) => p.id === id) ?? null
}
